import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'

import { LinearProgress } from '../components'

// Query to get redirect information
const GET_FUND_REDIRECT = gql`
  query getFundRedirect($fundId: Int!) {
    fundRedirectByOldFundId(oldFundId: $fundId) {
      charityId
      type
    }
  }
`

/**
 * GenerosityWorksRedirect Component
 *
 * Purpose: Redirects old Generosity Fund URLs to new Charity Fund URLs
 * Ticket: GW-870
 *
 * This component handles two URL patterns:
 * 1. /give/:fundId (from route params)
 * 2. /generosity-works-redirect?fundId=XXX (from query params)
 *
 * Implementation notes:
 * - Uses the FundRedirect table to map old fund IDs to charity IDs
 * - The redirect works even if the Generosity Fund has been deactivated
 * - Uses React Router's Navigate component with replace=true
 *   to ensure proper browser history management
 */

export function GenerosityWorksRedirect() {
  // Get fundId from either route params (/give/:fundId) or query params (?fundId=XXX)
  const { fundId: paramFundId } = useParams()
  const [searchParams] = useSearchParams()
  const queryFundId = searchParams.get('fundId')

  // Use whichever fundId is available
  const fundId = paramFundId || queryFundId

  // Query to check if this fund ID has a redirect
  const { data, loading, error } = useQuery(GET_FUND_REDIRECT, {
    variables: { fundId: Number(fundId) },
    // Skip query if fundId is not a valid number
    skip: !fundId || Number.isNaN(Number(fundId)),
  })

  // Show loading state while checking for redirect
  if (loading) return <LinearProgress />

  // Debug logging
  console.log('Redirect query result:', { fundId, data, error })

  // If there's an error or no redirect found, render the normal Generosity Fund GiveGenius page
  if (error || !data?.fundRedirectByOldFundId?.charityId) {
    return <Navigate to={`/gift/genfund/${fundId}`} replace />
  }

  // Redirect based on type
  const redirect = data.fundRedirectByOldFundId

  if (redirect.type === 'FUND_TO_FUND' && redirect.charityId.startsWith('FUND_')) {
    // Extract fund ID from FUND_123 format and redirect to another fund
    const targetFundId = redirect.charityId.replace('FUND_', '')
    return <Navigate to={`/give/${targetFundId}`} replace />
  }

  // Default: redirect to charity
  return <Navigate to={`/gift/charity/${redirect.charityId}`} replace />
}
